import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import useCallDisposition from '../../api/useCallDisposition';
import { CallDispositionType } from '../../types';
import * as Style from './style.module.scss';

// HWM-22633: SRS-locked set of 3 outcomes — surfaced via DefaultSelect now
// per the round-2 redesign.
const DISPOSITION_OPTIONS: { value: CallDispositionType; label: string }[] = [
    { value: 'connected', label: 'Connected' },
    { value: 'no_answer', label: 'No answer' },
    { value: 'left_voicemail', label: 'Left voicemail' }
];

interface CallDispositionModalProps {
    leadId: string;
    // patientName retained for backwards-compat with existing callers but
    // intentionally not rendered — round-2 design drops the name from the title.
    patientName?: string;
    onClose: () => void;
}

const CallDispositionModal: React.FC<CallDispositionModalProps> = ({ leadId, onClose }) => {
    const [disposition, setDisposition] = React.useState<CallDispositionType | ''>('');
    const [notes, setNotes] = React.useState('');

    const { mutateAsync: logCall, isPending } = useCallDisposition(onClose);

    const handleSubmit = async () => {
        if (!disposition) return;
        await logCall({
            payload: {
                lead_id: leadId,
                outcome: disposition,
                note: notes.trim() || undefined
            }
        });
    };

    return (
        <Modal isCancelable onClose={onClose} className={Style.modalSmall}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    Call Disposition
                </H>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Outcome
                    </P>
                    <select
                        name="disposition"
                        value={disposition}
                        onChange={(e) =>
                            setDisposition(e.currentTarget.value as CallDispositionType)
                        }
                        className={Style.selectInput}
                        data-placeholder={disposition ? 'false' : 'true'}
                    >
                        <option value="" disabled>
                            Select from list
                        </option>
                        {DISPOSITION_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        New note
                    </P>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className={Style.textarea}
                        placeholder="Add notes about the call…"
                    />
                </div>

                <Section noDefaultPadding className={Style.actionsCentered}>
                    <Button variant="outlineSecondary" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} disabled={!disposition || isPending}>
                        {isPending ? 'Saving…' : 'Save'}
                    </Button>
                </Section>
            </div>
        </Modal>
    );
};

export default CallDispositionModal;
